import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LogErrors } from './../../../../utilities/LogErrors'
import { ValidateSession } from './../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'
import { SaveCourseToShow } from './../../../../utilities/BL/Navigation'

import { InstructorBase } from './../../../Layouts/Instructor/V2/InstructorBase'
import { Search } from './../../../Common/Tables/Search'
import { Loading } from './../../../Common/Loading'
import { BuildAndDownloadWorkbook, BuildSheetWithMeta } from './../../../../utilities/BL/Instructor/ExcelExporter'
import { HELP_TYPE } from './../../../../utilities/EL/HelpType'
import { WEBPAGES } from './../../../../utilities/EL/WebPages'
import { ROLES } from '../../../../utilities/EL/Roles'

// --- BL de Líderes (Reutilizamos las listas de Results y usamos las nuevas de Progress) ---
import { GetInstructorsList, GetCoursesForSelectedGroup } from './../../../../utilities/BL/Leader/Results'
import { GetCourseProgressSummaryForLeader, GetCourseProgressDetailForLeader } from './../../../../utilities/BL/Leader/Progress'

// --- BL Reutilizado de Instructor ---
import { GetGroupZtudiantes as BLGetGroupZtudiantes } from './../../../../utilities/BL/Leader/Progress'
import { GetChoosenCourse } from './../../../../utilities/BL/Student/Courses'


const rolesData = {
    ZTUDIANTE: 1,
    INSTRUCTOR: 2,
    PROGRESS: 3
}

// Clave de storage exclusiva para Líderes para no chocar con la del Instructor
const LEADER_PROGRESS_STORAGE_KEY = process.env.REACT_APP_LEADER_PROGRESS_VIEW || 'LeaderProgressViewData'


const Progress = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()

    const [session, setSession] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isWorking, setIsWorking] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)

    // =========================================================================
    // 1. ESTADOS PARA FILTROS EN CASCADA (Instructor -> Grupo -> Curso)
    // =========================================================================
    const [instructorsList, setInstructorsList] = useState([])
    const [selectedInstructorId, setSelectedInstructorId] = useState(0)
    const [isInstructorsListLoading, setIsInstructorsListLoading] = useState(false)

    const [groupsList, setGroupsList] = useState([])
    const [selectedGroupId, setSelectedGroupId] = useState(0)

    const [coursesList, setCoursesList] = useState([])
    const [selectedCourseId, setSelectedCourseId] = useState(0)
    const [isCoursesListLoading, setIsCoursesListLoading] = useState(false)

    // =========================================================================
    // 2. ESTADOS DE TABLA Y BÚSQUEDA
    // =========================================================================
    const [allRows, setAllRows] = useState([])
    const [filteredRows, setFilteredRows] = useState([])
    const [searchText, setSearchText] = useState('')
    const allRowsRef = useRef([])

    const SHOW_GENERAL_PROGRESS_COLUMN = true

    useEffect(() => { allRowsRef.current = allRows }, [allRows])

    // Filtro local de buscador
    useEffect(() => {
        const text = (searchText ?? '').toLowerCase()
        if (!text) {
            setFilteredRows(allRows)
            return
        }

        const filtered = allRows.filter(r => {
            const fields = [`${r?.name ?? ''} ${r?.lastName ?? ''}`, r?.user]
            return fields.some(v => String(v ?? '').toLowerCase().includes(text))
        })
        setFilteredRows(filtered)
    }, [searchText, allRows])


    // =========================================================================
    // 3. HELPERS DE SELECCIÓN
    // =========================================================================
    const getSelectedInstructor = () => (instructorsList ?? []).find(i => i.idInstructor === selectedInstructorId) ?? null
    const getSelectedGroup = () => (groupsList ?? []).find(g => g.idGroup === selectedGroupId) ?? null
    const getSelectedCourse = () => (coursesList ?? []).find(c => c.idCourse === selectedCourseId) ?? null


    // =========================================================================
    // 4. PERSISTENCIA EN SESSION STORAGE
    // =========================================================================
    const restoreProgressState = () => {
        try {
            const raw = sessionStorage.getItem(LEADER_PROGRESS_STORAGE_KEY)
            if (!raw) return false

            const parsed = JSON.parse(raw)
            if (!parsed || !Array.isArray(parsed.instructorsList) || parsed.instructorsList.length === 0) {
                return false
            }

            setInstructorsList(parsed.instructorsList)
            setGroupsList(parsed.groupsList ?? [])
            setCoursesList(parsed.coursesList ?? [])
            
            setSelectedInstructorId(parsed.selectedInstructorId ?? 0)
            setSelectedGroupId(parsed.selectedGroupId ?? 0)
            setSelectedCourseId(parsed.selectedCourseId ?? 0)
            
            setSearchText(parsed.searchText ?? '')
            setAllRows(parsed.allRows ?? [])
            setFilteredRows(parsed.allRows ?? [])
            setErrorMessage(null)

            return true
        } catch (error) {
            LogErrors && LogErrors('restoreProgressState - Leader Progress', error)
            return false
        }
    }

    useEffect(() => {
        if (isLoading) return
        try {
            const snapshot = {
                instructorsList, selectedInstructorId,
                groupsList, selectedGroupId,
                coursesList, selectedCourseId,
                searchText, allRows
            }
            sessionStorage.setItem(LEADER_PROGRESS_STORAGE_KEY, JSON.stringify(snapshot))
        } catch (error) {
            LogErrors && LogErrors('persistProgressState - Leader Progress', error)
        }
    }, [selectedInstructorId, selectedGroupId, selectedCourseId, searchText, allRows, instructorsList, groupsList, coursesList, isLoading])


    // =========================================================================
    // 5. CARGA INICIAL
    // =========================================================================
    const LoadInitialData = async () => {
        try {
            setIsWorking(true)
            setErrorMessage(null)
            setIsInstructorsListLoading(true)

            const response = await GetInstructorsList()
            if (response.success) {
                setInstructorsList(response.data)
            } else {
                setErrorMessage(response?.message ?? 'No fue posible cargar los instructores.')
            }
        } catch (error) {
            await LogErrors('LoadInitialData - Leader Progress', error)
            setErrorMessage('Ocurrió un error al cargar la información inicial.')
        } finally {
            setIsWorking(false)
            setIsLoading(false)
            setIsInstructorsListLoading(false)
        }
    }

    useEffect(() => {
        async function ValidateAndInit() {
            const tmpSession = await ValidateSession()
            if (tmpSession?.success) {
                if (tmpSession.data?.user?.userStatus === 2) { // Asegurar estatus activo
                    setSession(tmpSession.data)
                    const restored = restoreProgressState()
                    if (!restored) {
                        await LoadInitialData()
                    } else {
                        setIsLoading(false)
                    }
                } else {
                    navigate('/confirm')
                }
            } else {
                navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
            }
        }
        ValidateAndInit()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    // =========================================================================
    // 6. HANDLERS PARA CASCADA DE SELECTORES
    // =========================================================================
    const handleSelectInstructor = (instructorId) => {
        setSelectedInstructorId(instructorId)
        
        // Limpiamos los niveles inferiores
        setSelectedGroupId(0)
        setSelectedCourseId(0)
        setGroupsList([])
        setCoursesList([])
        setAllRows([])
        setFilteredRows([])
        setSearchText('')

        if (!instructorId) return

        // Extraer los grupos localmente del objeto de instructor
        const selectedInstructor = instructorsList.find(inst => inst.idInstructor === instructorId)
        if (selectedInstructor && selectedInstructor.groups) {
            setGroupsList(selectedInstructor.groups)
        }
    }

    const handleSelectGroup = async (groupId) => {
        try {
            setSelectedGroupId(groupId)
            
            // Limpiamos los niveles inferiores
            setSelectedCourseId(0)
            setCoursesList([])
            setAllRows([])
            setFilteredRows([])
            setSearchText('')

            if (!groupId || !selectedInstructorId) return

            setIsCoursesListLoading(true)
            setIsWorking(true)
            setErrorMessage(null)

            const response = await GetCoursesForSelectedGroup(selectedInstructorId, groupId)
            if (response.success) {
                setCoursesList(response.data || [])
            } else {
                setErrorMessage(response?.message ?? 'No fue posible cargar los cursos asociados.')
            }
        } catch (e) {
            await LogErrors('handleSelectGroup - Leader Progress', e)
            setErrorMessage('Error interno al cargar los cursos.')
        } finally {
            setIsWorking(false)
            setIsCoursesListLoading(false)
        }
    }

    const handleSelectCourse = (courseId) => {
        setSelectedCourseId(courseId)
        setAllRows([])
        setFilteredRows([])
        setSearchText('')
    }


    // =========================================================================
    // 7. APLICAR FILTRO: OBTENER ALUMNOS Y SU PROGRESO
    // =========================================================================
    const GetFilteredStudents = async () => {
        try {
            if (!selectedInstructorId || !selectedGroupId || !selectedCourseId) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Completa todos los filtros para consultar.' })
                return
            }

            setIsWorking(true)
            setIsLoading(true)
            setErrorMessage(null)
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Obteniendo progreso de Ztudiantes...' })

            // 1. Obtener la lista base de Ztudiantes usando el EndPoint reutilizado
            const listResp = await BLGetGroupZtudiantes(selectedInstructorId, selectedGroupId)

            if (!listResp?.success) {
                setAllRows([])
                setFilteredRows([])
                showMessage({ type: MESSAGE_TYPE.BAD, text: listResp?.message ?? 'No ha sido posible obtener el listado.' })
                return
            }

            const students = Array.isArray(listResp.data) ? listResp.data : (listResp.data?.ztudiantes ?? [])

            let rows = (students ?? []).map(s => ({
                idZtudiante: s.idZtudiante ?? s.id ?? s.studentId,
                name: s.name ?? '',
                lastName: s.lastName ?? '',
                user: s.user ?? s.email ?? '',
                generalProgress: '',
                blocks: s.blocks ?? []
            }))

            // 2. Enriquecer con los porcentajes de avance
            try {
                const userIds = rows.map(r => r.idZtudiante)

                if (userIds.length > 0) {
                    const summaryResp = await GetCourseProgressSummaryForLeader(
                        selectedInstructorId, selectedGroupId, selectedCourseId, userIds
                    )

                    if (summaryResp?.success && summaryResp.data && summaryResp.data.progress) {
                        const progressArray = Array.isArray(summaryResp.data.progress) ? summaryResp.data.progress : []
                        const progressMap = {}
                        
                        progressArray.forEach(item => {
                            const idUser = item.idUser
                            const percentage = item.summary?.percentageApproved ?? 0
                            progressMap[idUser] = `${Number(percentage)}%`
                        })
                        
                        rows = rows.map(r => ({ ...r, generalProgress: progressMap[r.idZtudiante] ?? '' }))
                    }
                }
            } catch (innerError) {
                LogErrors && LogErrors('GetFilteredStudents - ProgressSummary', innerError)
            }

            setAllRows(rows)
            setFilteredRows(rows)
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mostrando progreso de Ztudiantes' })
        } catch (error) {
            await LogErrors('GetFilteredStudents - Progress', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ocurrió un error al cargar el progreso.' })
            setAllRows([])
            setFilteredRows([])
        } finally {
            setIsWorking(false)
            setIsLoading(false)
        }
    }


    // =========================================================================
    // 8. NAVEGACIÓN Y EXPORTACIÓN A EXCEL
    // =========================================================================
    const GoToNextView = async (row) => {
        try {
            setIsWorking(true)
            setErrorMessage(null)

            const selectedCourse = getSelectedCourse()
            if (!selectedCourse) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona un curso válido' })
                return
            }

            const coursePayload = {
                idCourse: selectedCourse.idCourse,
                nameCourse: selectedCourse.nameCourse,
                idLevel: selectedCourse.idLevel,
                idType: selectedCourse.idType,
                goToQuickAccessInstrument: false,
                idUser: row.idZtudiante,
                idInstructor: selectedInstructorId,
                urlToBack: '/leader/progress'
            }

            // Almacenar el snapshot antes de la redirección
            try {
                const snapshot = {
                    instructorsList, selectedInstructorId,
                    groupsList, selectedGroupId,
                    coursesList, selectedCourseId,
                    searchText, allRows
                }
                sessionStorage.setItem(LEADER_PROGRESS_STORAGE_KEY, JSON.stringify(snapshot))
            } catch (error) {
                LogErrors && LogErrors('SaveStateBeforeGoToNextView - Progress', error)
            }

            const resp = await SaveCourseToShow(session, coursePayload, false, rolesData.PROGRESS)

            if (resp?.success) {
                navigate('/course')
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: resp?.message ?? 'No fue posible abrir el curso' })
            }
        } catch (error) {
            await LogErrors('GoToNextView - Progress', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ocurrió un error al abrir la vista del curso' })
        } finally {
            setIsWorking(false)
        }
    }

    const ExportGroupTableToExcel = () => {
        try {
            const instructor = getSelectedInstructor()
            const course = getSelectedCourse()
            const group = getSelectedGroup()

            if (!instructor || !course || !group) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona filtros antes de exportar.' })
                return
            }

            if (!filteredRows || filteredRows.length === 0) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay registros para exportar.' })
                return
            }

            const metaRows = [
                ['Instructor:', instructor.instructorName ?? ''],
                ['Curso:', `${course.nameCourse ?? ''}${course.level ? ` (Nivel ${course.level})` : ''}`],
                ['Grupo:', group.groupName ?? ''],
                ['Total:', filteredRows.length.toString()],
                []
            ]

            const headerRow = SHOW_GENERAL_PROGRESS_COLUMN
                ? ['Nombre', 'Usuario', 'Avance general']
                : ['Nombre', 'Usuario']

            const bodyRows = filteredRows.map(r => {
                const fullName = `${r.name ?? ''} ${r.lastName ?? ''}`.trim()
                const generalProgress = r.generalProgress ?? r.progress ?? r.advance ?? ''
                return SHOW_GENERAL_PROGRESS_COLUMN
                    ? [fullName, r.user ?? '', generalProgress]
                    : [fullName, r.user ?? '']
            })

            const rows = [headerRow, ...bodyRows]
            const ws = BuildSheetWithMeta({
                metaRows,
                rows,
                colWidths: SHOW_GENERAL_PROGRESS_COLUMN ? [35, 25, 18] : [35, 25]
            })

            const now = new Date()
            const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`

            const safeCourseName = (course.nameCourse ?? 'curso').replace(/[\\/:*?"<>|]/g, '_')
            const safeGroupName = (group.groupName ?? 'grupo').replace(/[\\/:*?"<>|]/g, '_')

            BuildAndDownloadWorkbook(
                `Progreso_${safeCourseName}_${safeGroupName}_${stamp}.xlsx`,
                [{ name: 'Progreso general', ws }]
            )
        } catch (error) {
            LogErrors && LogErrors('ExportGroupTableToExcel - Progress', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al intentar exportar el listado.' })
        }
    }

    const ExportIndividualToExcel = async (row) => {
        try {
            const instructor = getSelectedInstructor()
            const course = getSelectedCourse()
            const group = getSelectedGroup()

            if (!instructor || !course || !group) return

            setIsWorking(true)
            const fullName = `${row.name ?? ''} ${row.lastName ?? ''}`.trim()

            let blocks = []
            let generalProgressValue = ''

            // Usamos el BL de Líderes para el detalle individual
            const progressResp = await GetCourseProgressDetailForLeader(selectedInstructorId, course.idCourse, row.idZtudiante)
            
            if (progressResp?.success) {
                const progress = progressResp.data?.progress
                const percentage = Number(progress?.summary?.percentageApproved ?? 0)
                generalProgressValue = `${percentage}%`

                if (progress?.byBlock) {
                    blocks = Object.values(progress.byBlock).map(b => ({
                        blockName: b.blockName ?? '',
                        advance: `${Number(b.percentageApproved ?? 0)}%`,
                        totalTopics: b.totalItem ?? 0,
                        approvedTopics: b.approvedCount ?? 0
                    }))
                }
            } else {
                generalProgressValue = row.generalProgress ?? row.progress ?? row.advance ?? ''
                blocks = []
            }

            const metaRows = [
                ['Instructor:', instructor.instructorName ?? ''],
                ['Curso:', `${course.nameCourse ?? ''}${course.level ? ` (Nivel ${course.level})` : ''}`],
                ['Grupo:', group.groupName ?? ''],
                ['Nombre:', fullName],
                ['Usuario:', row.user ?? ''],
                ['Avance general:', generalProgressValue],
                [],
            ]

            const headerRow = ['Bloque', 'Avance', 'Temas del bloque', 'Temas aprobados']

            const visibleBlocks = (blocks ?? []).filter(b => {
                const name = b.blockName?.trim().toLowerCase() ?? ''
                return !name.startsWith('evaluación')
            })

            const bodyRows = visibleBlocks.map(b => [
                b.blockName ?? '', b.advance ?? '', b.totalTopics ?? '', b.approvedTopics ?? ''
            ])

            const rows = [headerRow, ...bodyRows]
            const ws = BuildSheetWithMeta({
                metaRows, rows, colWidths: [40, 15, 20, 20]
            })

            const now = new Date()
            const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`

            const safeCourseName = (course.nameCourse ?? 'curso').replace(/[\\/:*?"<>|]/g, '_')
            const safeUser = (row.user ?? 'ztudiante').replace(/[\\/:*?"<>|]/g, '_')

            BuildAndDownloadWorkbook(
                `Progreso_${safeCourseName}_${safeUser}_${stamp}.xlsx`,
                [{ name: 'Progreso individual', ws }]
            )
        } catch (error) {
            LogErrors && LogErrors('ExportIndividualToExcel - Progress', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al intentar exportar el informe individual.' })
        } finally {
            setIsWorking(false)
        }
    }


    // =========================================================================
    // 9. RENDER DE LA INTERFAZ
    // =========================================================================
    // La flecha de regreso sale por completo de esta vista (a diferencia de "Ver más",
    // que solo navega a /course y puede volver), así que aquí sí debe limpiarse el filtro
    // guardado para que la próxima vez que se entre a la página se muestre en blanco.
    const HandleGoBack = () => {
        try {
            sessionStorage.removeItem(LEADER_PROGRESS_STORAGE_KEY)
        } catch (error) {
            LogErrors && LogErrors('HandleGoBack - Leader Progress', error)
        }
        navigate('/dashboard')
    }

    return (
        isLoading ? (
            <Loading />
        ) : (
            <InstructorBase urlToGo={null} idHelp={HELP_TYPE.LEADER.PROGRESS} page={WEBPAGES.LEADER.PROGRESS} role={ROLES.LEADER}>
                <div className='container InstructorBase__Main__Return'>
                    <button type='button' onClick={HandleGoBack} className='Btn__Ztudia--Link InstructorBase__Main__Return__Btn'>
                        <i className='bi bi-caret-left-fill'></i>
                    </button>
                </div>

                <section className='container Instructor__Reports'>
                    <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>Progreso de Ztudiantes</p>

                    {/* CASCADA DE FILTROS */}
                    <div className='Instructor__Progress__Filters Instructor__SpaceUp'>
                        
                        {/* 1. INSTRUCTOR */}
                        {isInstructorsListLoading ? (
                            <select className='Instructor__Progress__Filters__List' disabled={true}>
                                <option>Cargando instructores...</option>
                            </select>
                        ) : (
                            <select
                                className='Instructor__Progress__Filters__List'
                                value={selectedInstructorId}
                                onChange={(e) => handleSelectInstructor(parseInt(e.target.value, 10))}
                                disabled={isWorking || instructorsList.length === 0}
                            >
                                <option value={0}>Selecciona un instructor</option>
                                {instructorsList.map(inst => (
                                    <option key={inst.idInstructor} value={inst.idInstructor}>{inst.instructorName}</option>
                                ))}
                            </select>
                        )}

                        {/* 2. GRUPO */}
                        <select
                            className='Instructor__Progress__Filters__List'
                            value={selectedGroupId}
                            onChange={(e) => handleSelectGroup(parseInt(e.target.value, 10))}
                            disabled={isWorking || !selectedInstructorId || groupsList.length === 0}
                        >
                            <option value={0}>Selecciona un grupo</option>
                            {groupsList.map(g => (
                                <option key={g.idGroup} value={g.idGroup}>{g.groupName}</option>
                            ))}
                        </select>

                        {/* 3. CURSO */}
                        {isCoursesListLoading ? (
                            <select className='Instructor__Progress__Filters__List' disabled={true}>
                                <option>Cargando cursos...</option>
                            </select>
                        ) : (
                            <select
                                className='Instructor__Progress__Filters__List'
                                value={selectedCourseId}
                                onChange={(e) => handleSelectCourse(parseInt(e.target.value, 10))}
                                disabled={isWorking || !selectedGroupId || coursesList.length === 0}
                            >
                                <option value={0}>Selecciona un curso</option>
                                {coursesList.map(c => (
                                    <option key={c.idCourse} value={c.idCourse}>{c.nameCourse}{c.level && ` (Nivel ${c.level})`}</option>
                                ))}
                            </select>
                        )}

                        {/* BOTÓN APLICAR */}
                        <button
                            type='button'
                            className='Btn__Ztudia--PrimaryOutline Instructor__Progress__Filters__Button'
                            onClick={GetFilteredStudents}
                            disabled={isWorking || !selectedInstructorId || !selectedGroupId || !selectedCourseId}
                        >
                            <i className='bi bi-funnel'></i> Aplicar filtro
                        </button>
                    </div>

                    {/* CONTROLES DE LA TABLA */}
                    <Search onChange={setSearchText} extraClassName={'Instructor__Reports__Ztudents__Search mt-5 mw-100'} />

                    <div className='Instructor__SpaceUp Instructor__Progress__ExportTable'>
                        <button
                            type='button'
                            className='Btn__Ztudia--PrimaryOutline Instructor__Progress__ExportTable__Button'
                            onClick={ExportGroupTableToExcel}
                            disabled={isWorking || !filteredRows || filteredRows.length === 0}
                        >
                            <i className='bi bi-file-earmark-excel'></i> Exportar
                        </button>
                    </div>

                    {/* TABLA PRINCIPAL */}
                    <section className='Tables__Container Instructor__Progress--V2__ContainerTable Tables--Shadow Tables--Background'>
                        <table className='Tables__Table Instructor__Progress--V2__ContainerTable__Table'>
                            <thead>
                                <tr className='Tables__Table__Row--Header'>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>Nombre</td>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>Usuario</td>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>Avance</td>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn'></td>
                                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn'></td>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows?.length ? (
                                    filteredRows.map((r, idx) => (
                                        <tr key={r.idZtudiante ?? idx} className='Tables__Table__Row'>
                                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>
                                                {`${r.name ?? ''} ${r.lastName ?? ''}`.trim()}
                                            </td>
                                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>
                                                {r.user}
                                            </td>
                                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>
                                                {r.generalProgress ?? ''}
                                            </td>
                                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn'>
                                                <div className='Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn__Buttons'>
                                                    <button type='button' className='Btn__Ztudia--PrimaryOutline Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn__Buttons__Button' onClick={() => GoToNextView(r)}>Ver más</button>
                                                </div>
                                            </td>
                                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn'>
                                                <div className='Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn__Buttons'>
                                                    <button type='button' className='Btn__Ztudia--PrimaryOutline Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn__Buttons__Button' onClick={() => ExportIndividualToExcel(r)} disabled={isWorking}><i className='bi bi-file-earmark-excel'></i> Exportar</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr className='Tables__Table__Row'>
                                        <td className='Tables__Table__Row__Cell text-center py-4' colSpan={5}>
                                            {isWorking ? 'Consultando progreso...' : 'Sin resultados por mostrar. Aplica los filtros.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </section>

                    <p className='Instructor__Subtitle Instructor__Subtitle--Center Instructor__SpaceUp'>
                        Total mostrados: {filteredRows?.length ?? 0}
                    </p>

                    {errorMessage && errorMessage !== '' && (
                        <p className='Instructor__Message--Error text-center mt-3'>{errorMessage}</p>
                    )}
                </section>
            </InstructorBase>
        )
    )
}

export { Progress };









// import { useEffect, useRef, useState } from 'react'
// import { useNavigate } from 'react-router-dom'

// import { LogErrors } from './../../../../../utilities/LogErrors'

// import { InstructorBase } from './../../../../Layouts/Instructor/V2/InstructorBase'
// import { Search } from './../../../../Common/Tables/Search'

// import { ValidateSession } from './../../../../../utilities/BL/Sessions'
// import { useFloatMessage, MESSAGE_TYPE } from './../../../../Providers/FloatMessage'
// import { SaveCourseToShow } from './../../../../../utilities/BL/Navigation'

// import { GetCourses as BLGetCourses, GetCourseProgressSummary as BLGetCourseProgressSummary } from './../../../../../utilities/BL/Instructor/Agendas'
// import { GetAllDataGroups as BLGetAllDataGroups, GetZtudentsOfGroup as BLGetZtudentsOfGroup } from './../../../../../utilities/BL/Instructor/Groups'

// import { Loading } from './../../../../Common/Loading'

// import { BuildAndDownloadWorkbook, BuildSheetWithMeta } from './../../../../../utilities/BL/Instructor/ExcelExporter'
// import { GetChoosenCourse, GetCourseProgressInstructor as BLGetCourseProgressInstructor } from './../../../../../utilities/BL/Student/Courses'
// import { HELP_TYPE } from './../../../../../utilities/EL/HelpType'
// import { WEBPAGES } from '../../../../../utilities/EL/WebPages'


// const rolesData = {
//   ZTUDIANTE: 1,
//   INSTRUCTOR: 2,
//   PROGRESS: 3
// }

// // Clave de storage para recordar el estado de la vista
// const PROGRESS_STORAGE_KEY = process.env.REACT_APP_INSTRUCTOR_PROGRESS_VIEW

// /**
//  * Normaliza el listado de cursos
//  */
// const normalizeCourses = (arr) => (arr ?? []).map(c => ({
//   id: c.idCourse,
//   name: c.nameCourse,
//   idLevel: c.idLevel,
//   idType: c.idType,
//   level: c.level ?? null
// }))

// /**
//  * Normaliza el response de grupos (visibles y no visibles) a un solo arreglo.
//  */
// const normalizeGroups = (resp) => {
//   if (!resp?.success) return []
//   return [
//     ...(resp?.data?.visibles ?? []).map(x => ({ ...x, visible: true })),
//     ...(resp?.data?.noVisibles ?? []).map(x => ({ ...x, visible: false }))
//   ]
// }

// const Progress = () => {
//   const navigate = useNavigate()
//   const { showMessage } = useFloatMessage()

//   const [session, setSession] = useState(null)

//   // UI y estado
//   const [isLoading, setIsLoading] = useState(true)
//   const [isWorking, setIsWorking] = useState(false)
//   const [errorMessage, setErrorMessage] = useState(null)

//   // Curso → Grupo
//   const [courses, setCourses] = useState(null)
//   const [selectedCourseId, setSelectedCourseId] = useState(0)
//   const [isCoursesListLoading, setIsCoursesListLoading] = useState(false)

//   const [groups, setGroups] = useState(null)
//   const [selectedGroupId, setSelectedGroupId] = useState(0)
//   const [isGroupsListLoading, setIsGroupsListLoading] = useState(false)

//   // Tabla + buscador
//   const [allRows, setAllRows] = useState([])
//   const [filteredRows, setFilteredRows] = useState([])
//   const [searchText, setSearchText] = useState('')
//   const allRowsRef = useRef([])

//   // Toggle para mostrar/ocultar la columna "Avance general" en el Excel de tabla
//   const SHOW_GENERAL_PROGRESS_COLUMN = true

//   useEffect(() => { allRowsRef.current = allRows }, [allRows])

//   // -------------------------------------------------
//   // Filtro por buscador (Nombre / Usuario)
//   // -------------------------------------------------
//   useEffect(() => {
//     const text = (searchText ?? '').toLowerCase()
//     if (!text) {
//       setFilteredRows(allRows)
//       return
//     }

//     const filtered = allRows.filter(r => {
//       const fields = [
//         `${r?.name ?? ''} ${r?.lastName ?? ''}`,
//         r?.user
//       ]
//       return fields.some(v => String(v ?? '').toLowerCase().includes(text))
//     })

//     setFilteredRows(filtered)
//   }, [searchText, allRows])

//   // -------------------------------------------------
//   // Helpers de curso / grupo seleccionados
//   // -------------------------------------------------
//   const getSelectedCourse = () =>
//     (courses ?? []).find(c => c.id === selectedCourseId) ?? null

//   const getSelectedGroup = () =>
//     (groups ?? []).find(g => g.idGroup === selectedGroupId) ?? null

//   // -------------------------------------------------
//   // Persistencia en sessionStorage
//   // -------------------------------------------------

//   /**
//    * Intenta restaurar el estado de la vista desde sessionStorage.
//    * Regresa true si se restauró algo, false en caso contrario.
//    */
//   const restoreProgressState = () => {
//     try {
//       const raw = sessionStorage.getItem(PROGRESS_STORAGE_KEY)
//       if (!raw) return false

//       const parsed = JSON.parse(raw)
//       if (!parsed) return false

//       // 👇 Punto clave:
//       // Si NO hay cursos válidos en el snapshot,
//       // consideramos que no hay nada útil que restaurar
//       if (!Array.isArray(parsed.courses) || parsed.courses.length === 0) {
//         return false
//       }

//       setCourses(parsed.courses)
//       setGroups(parsed.groups ?? null)
//       setSelectedCourseId(parsed.selectedCourseId ?? 0)
//       setSelectedGroupId(parsed.selectedGroupId ?? 0)
//       setSearchText(parsed.searchText ?? '')
//       setAllRows(parsed.allRows ?? [])
//       setFilteredRows(parsed.allRows ?? [])
//       setErrorMessage(null)

//       return true
//     } catch (error) {
//       LogErrors && LogErrors('restoreProgressState - Progress', error)
//       return false
//     }
//   }

//   /**
//    * Guarda en sessionStorage el estado actual de la vista.
//    * Se dispara automáticamente cada que cambian filtros o la tabla.
//    */
//   useEffect(() => {
//     // Mientras está cargando por primera vez no guardamos nada
//     if (isLoading) return

//     try {
//       const snapshot = {
//         selectedCourseId,
//         selectedGroupId,
//         searchText,
//         allRows,
//         courses,
//         groups
//       }
//       sessionStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(snapshot))
//     } catch (error) {
//       LogErrors && LogErrors('persistProgressState - Progress', error)
//     }
//   }, [selectedCourseId, selectedGroupId, searchText, allRows, courses, groups, isLoading])

//   // -------------------------------------------------
//   // Navegar a curso (Ver más)
//   // -------------------------------------------------
//   const GoToNextView = async (row) => {
//     try {
//       setIsWorking(true)
//       setErrorMessage(null)

//       const selectedCourse = getSelectedCourse()
//       if (!selectedCourse) {
//         showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona un curso válido' })
//         return
//       }

//       const coursePayload = {
//         idCourse: selectedCourse.id,
//         nameCourse: selectedCourse.name,
//         idLevel: selectedCourse.idLevel,
//         idType: selectedCourse.idType,
//         goToQuickAccessInstrument: false,
//         idUser: row.idZtudiante
//       }

//       // Guardar estado actual antes de navegar (por si acaso)
//       try {
//         const snapshot = {
//           selectedCourseId,
//           selectedGroupId,
//           searchText,
//           allRows,
//           courses,
//           groups
//         }
//         sessionStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(snapshot))
//       } catch (error) {
//         LogErrors && LogErrors('SaveStateBeforeGoToNextView - Progress', error)
//       }

//       const resp = await SaveCourseToShow(session, coursePayload, false, rolesData.PROGRESS)

//       if (resp?.success) {
//         navigate('/course')
//       } else {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: resp?.message ?? 'No fue posible abrir el curso'
//         })
//       }
//     } catch (error) {
//       await LogErrors('GoToNextView - Progress', error)
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: 'Ocurrió un error al abrir la vista del curso'
//       })
//     } finally {
//       setIsWorking(false)
//     }
//   }

//   // -------------------------------------------------
//   // Exportar a Excel (tabla completa)
//   // -------------------------------------------------
//   const ExportGroupTableToExcel = () => {
//     try {
//       const course = getSelectedCourse()
//       const group = getSelectedGroup()

//       if (!course || !group) {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: 'Selecciona curso y grupo antes de exportar.'
//         })
//         return
//       }

//       if (!filteredRows || filteredRows.length === 0) {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: 'No hay registros para exportar.'
//         })
//         return
//       }

//       // Meta (parte superior del Excel)
//       const metaRows = [
//         ['Curso:', course.name ?? ''],
//         ['Grupo:', group.groupName ?? ''],
//         ['Total:', filteredRows.length.toString()],
//         [] // fila vacía separadora
//       ]

//       // ---------- Cabeceras ----------
//       const headerRow = SHOW_GENERAL_PROGRESS_COLUMN
//         ? ['Nombre', 'Usuario', 'Avance general']
//         : ['Nombre', 'Usuario']

//       // ---------- Filas de datos ----------
//       const bodyRows = filteredRows.map(r => {
//         const fullName = `${r.name ?? ''} ${r.lastName ?? ''}`.trim()
//         const generalProgress = r.generalProgress ?? r.progress ?? r.advance ?? ''

//         return SHOW_GENERAL_PROGRESS_COLUMN
//           ? [fullName, r.user ?? '', generalProgress]
//           : [fullName, r.user ?? '']
//       })

//       const rows = [headerRow, ...bodyRows]

//       const ws = BuildSheetWithMeta({
//         metaRows,
//         rows,
//         colWidths: SHOW_GENERAL_PROGRESS_COLUMN
//           ? [35, 25, 18]
//           : [35, 25]
//       })

//       const now = new Date()
//       const stamp = `${now.getFullYear()}${String(
//         now.getMonth() + 1
//       ).padStart(2, '0')}${String(now.getDate()).padStart(
//         2,
//         '0'
//       )}_${String(now.getHours()).padStart(2, '0')}${String(
//         now.getMinutes()
//       ).padStart(2, '0')}`

//       const safeCourseName = (course.name ?? 'curso').replace(/[\\/:*?"<>|]/g, '_')
//       const safeGroupName = (group.groupName ?? 'grupo').replace(/[\\/:*?"<>|]/g, '_')

//       BuildAndDownloadWorkbook(
//         `Progreso_${safeCourseName}_${safeGroupName}_${stamp}.xlsx`,
//         [
//           {
//             name: 'Progreso general',
//             ws
//           }
//         ]
//       )
//     } catch (error) {
//       LogErrors && LogErrors('ExportGroupTableToExcel - Progress', error)
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: 'Ha habido un error al intentar exportar el listado.'
//       })
//     }
//   }

//   // -------------------------------------------------
//   // Exportar a Excel (informe individual)
//   // -------------------------------------------------
//   const ExportIndividualToExcel = async (row) => {
//     try {
//       const course = getSelectedCourse()
//       const group = getSelectedGroup()

//       if (!course || !group) {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: 'Selecciona curso y grupo antes de exportar.'
//         })
//         return
//       }

//       if (!row) {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: 'No se ha encontrado la información del Ztudiante.'
//         })
//         return
//       }

//       setIsWorking(true)

//       const totalZtudents = allRows?.length ?? 0
//       const fullName = `${row.name ?? ''} ${row.lastName ?? ''}`.trim()

//       // ---------- 1) Preparar navegación del curso/ztudiante ----------
//       const coursePayload = {
//         idCourse: course.id,
//         nameCourse: course.name,
//         idLevel: course.idLevel,
//         idType: course.idType,
//         goToQuickAccessInstrument: false,
//         idUser: row.idZtudiante
//       }

//       const saveResp = await SaveCourseToShow(session, coursePayload, false, rolesData.PROGRESS)
//       if (!saveResp?.success) {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: saveResp?.message ?? 'No fue posible preparar la información del curso para el reporte.'
//         })
//         return
//       }

//       // ---------- 2) Obtener curso elegido (como en Course.js) ----------
//       const chosenResp = await GetChoosenCourse(rolesData.INSTRUCTOR)
//       if (!chosenResp?.success) {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: chosenResp?.message ?? 'No fue posible obtener la información del curso.'
//         })
//         return
//       }

//       // ---------- 3) Obtener progreso del curso para ese ztudiante ----------
//       let blocks = []
//       let generalProgressValue = ''

//       const progressResp = await BLGetCourseProgressInstructor(row.idZtudiante)
//       if (progressResp?.success) {
//         const progress = progressResp.data?.progress

//         // Avance general (summary)
//         const percentage = Number(progress?.summary?.percentageApproved ?? 0)
//         generalProgressValue = `${percentage}%`

//         // Detalle por bloque → filas para Excel
//         if (progress?.byBlock) {
//           blocks = Object.values(progress.byBlock).map(b => ({
//             blockName: b.blockName ?? '',
//             advance: `${Number(b.percentageApproved ?? 0)}%`,
//             totalTopics: b.totalItem ?? 0,
//             approvedTopics: b.approvedCount ?? 0
//           }))
//         }
//       } else {
//         // Si no hay progreso, usamos lo que venga en la fila (si existe)
//         generalProgressValue = row.generalProgress ?? row.progress ?? row.advance ?? ''
//         blocks = []
//       }

//       // ---------- 4) Meta para informe individual ----------
//       const metaRows = [
//         ['Curso:', course.name ?? ''],
//         ['Grupo:', group.groupName ?? ''],
//         ['Nombre:', fullName],
//         ['Usuario:', row.user ?? ''],
//         ['Avance general:', generalProgressValue],
//         [], // separador
//       ]

//       // Encabezados de detalle por bloque
//       const headerRow = ['Bloque', 'Avance', 'Temas del bloque', 'Temas aprobados']

//       // -------------------------------------------
//       // FILTRAR BLOQUES QUE NO SEAN "Evaluación"
//       // (Es decir: ocultamos todo lo que empiece con "Evaluación")
//       // -------------------------------------------
//       const visibleBlocks = (blocks ?? []).filter(b => {
//         const name = b.blockName?.trim().toLowerCase() ?? ''
//         return !name.startsWith('evaluación')
//       })

//       // Si no quieres buscar acentos, puedes usar startsWith('evaluacion')
//       // usando normalize:
//       // const normalized = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
//       // return !normalized.startsWith("evaluacion")

//       // -------------------------------------------
//       // FILAS DEL EXCEL
//       // -------------------------------------------
//       const bodyRows = visibleBlocks.map(b => [
//         b.blockName ?? '',
//         b.advance ?? '',
//         b.totalTopics ?? '',
//         b.approvedTopics ?? ''
//       ])

//       const rows = [headerRow, ...bodyRows]

//       const ws = BuildSheetWithMeta({
//         metaRows,
//         rows,
//         colWidths: [40, 15, 20, 20]
//       })

//       const now = new Date()
//       const stamp = `${now.getFullYear()}${String(
//         now.getMonth() + 1
//       ).padStart(2, '0')}${String(now.getDate()).padStart(
//         2,
//         '0'
//       )}_${String(now.getHours()).padStart(2, '0')}${String(
//         now.getMinutes()
//       ).padStart(2, '0')}`

//       const safeCourseName = (course.name ?? 'curso').replace(/[\\/:*?"<>|]/g, '_')
//       const safeUser = (row.user ?? 'ztudiante').replace(/[\\/:*?"<>|]/g, '_')

//       BuildAndDownloadWorkbook(
//         `Progreso_${safeCourseName}_${safeUser}_${stamp}.xlsx`,
//         [
//           {
//             name: 'Progreso individual',
//             ws
//           }
//         ]
//       )
//     } catch (error) {
//       LogErrors && LogErrors('ExportIndividualToExcel - Progress', error)
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: 'Ha habido un error al intentar exportar el informe individual.'
//       })
//     } finally {
//       setIsWorking(false)
//     }
//   }

//   // -------------------------------------------------
//   // Carga inicial (cursos) + restaurar estado si existe
//   // -------------------------------------------------
//   const LoadPage = async () => {
//     try {
//       setIsWorking(true)
//       setErrorMessage(null)
//       setIsCoursesListLoading(true)

//       const response = await BLGetCourses()
//       if (response?.success) {
//         setCourses(normalizeCourses(response.data))
//       } else {
//         setErrorMessage(response?.message ?? 'No fue posible cargar cursos.')
//       }
//     } catch (error) {
//       await LogErrors('LoadPage - Progress', error)
//       setErrorMessage('Ocurrió un error al cargar cursos.')
//     } finally {
//       setIsWorking(false)
//       setIsLoading(false)
//       setIsCoursesListLoading(false)
//     }
//   }

//   useEffect(() => {
//     async function Validate() {
//       const tmpSession = await ValidateSession()
//       if (tmpSession?.success) {
//         if (tmpSession.data?.user?.userStatus === 2) {
//           setSession(tmpSession.data)

//           // 1) Intentar restaurar estado desde sessionStorage
//           const restored = restoreProgressState()

//           // 2) Si no hay nada que restaurar, cargamos cursos desde cero
//           if (!restored) {
//             await LoadPage()
//           } else {
//             setIsLoading(false)
//           }
//         } else {
//           navigate('/confirm')
//         }
//       } else {
//         navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
//       }
//     }
//     Validate()
//   }, [])

//   // -------------------------------------------------
//   // Encadenado: curso → grupos
//   // -------------------------------------------------
//   const handleSelectCourse = async (courseId) => {
//     try {
//       setIsGroupsListLoading(true)
//       setSelectedCourseId(courseId)
//       setSelectedGroupId(0)
//       setGroups(null)
//       setAllRows([])
//       setFilteredRows([])
//       setSearchText('')

//       if (!courseId) return

//       setIsWorking(true)
//       setErrorMessage(null)

//       const response = await BLGetAllDataGroups()
//       if (response?.success) {
//         setGroups(normalizeGroups(response))
//       } else {
//         setErrorMessage(response?.message ?? 'No fue posible cargar el listado de grupo. Actualiza la página para intentar solucionarlo')
//       }
//     } catch (e) {
//       await LogErrors('handleSelectCourse - Progress', e)
//       setErrorMessage('Ha habido un error al intentar cargar el listado de grupos. Actualiza la página para intentar solucionarlo')
//     } finally {
//       setIsWorking(false)
//       setIsGroupsListLoading(false)
//     }
//   }

//   const handleSelectGroup = async (groupId) => {
//     setSelectedGroupId(groupId)
//     setAllRows([])
//     setFilteredRows([])
//     setSearchText('')
//   }

//   // -------------------------------------------------
//   // Aplicar filtro: obtener SOLO alumnos del grupo
//   // -------------------------------------------------
//   const GetFilteredStudents = async () => {
//     try {
//       if (!selectedCourseId || !selectedGroupId) {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: 'Selecciona curso y grupo para aplicar el filtro'
//         })
//         return
//       }

//       setIsWorking(true)
//       setIsLoading(true)
//       setErrorMessage(null)
//       showMessage({
//         type: MESSAGE_TYPE.INFO,
//         text: 'Obteniendo Ztudiantes...'
//       })

//       const listResp = await BLGetZtudentsOfGroup({ id: selectedGroupId })
//       if (!listResp?.success) {
//         setAllRows([])
//         setFilteredRows([])
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: listResp?.message ?? 'No ha sido posible obtener el listado de Ztudiantes'
//         })
//         return
//       }

//       const students = Array.isArray(listResp.data)
//         ? listResp.data
//         : (listResp.data?.ztudiantes ?? [])

//       // 1) Construimos las filas base (sin avance aún)
//       let rows = (students ?? []).map(s => ({
//         idZtudiante: s.idZtudiante ?? s.id ?? s.studentId,
//         name: s.name ?? '',
//         lastName: s.lastName ?? '',
//         user: s.user ?? s.email ?? '',
//         // estos campos se van a rellenar/enriquecer con el endpoint nuevo
//         generalProgress: '',
//         blocks: s.blocks ?? []
//       }))

//       // 2) Obtenemos el porcentaje de avance desde BLGetCourseProgressSummary
//       try {
//         const userIds = rows.map(r => r.idZtudiante)

//         const summaryResp = await BLGetCourseProgressSummary(
//           selectedGroupId,   // idGroup
//           selectedCourseId,  // idCourse
//           userIds            // [3, 4, 5, ...]
//         )

//         if (summaryResp?.success && summaryResp.data && summaryResp.data.progress) {
//           const progressArray = Array.isArray(summaryResp.data.progress)
//             ? summaryResp.data.progress
//             : []

//           const progressMap = {}
          
//           progressArray.forEach(item => {
//             const idUser = item.idUser
//             const percentage = item.summary?.percentageApproved ?? 0
//             progressMap[idUser] = `${Number(percentage)}%`
//           })
          
//           // 3) Enriquecemos las filas con el porcentaje
//           rows = rows.map(r => ({
//             ...r,
//             generalProgress: progressMap[r.idZtudiante] ?? ''
//           }))
//         }
//       } catch (innerError) {
//         // Si falla el resumen, no tronamos la página; solo seguimos sin progreso
//         LogErrors && LogErrors('GetFilteredStudents - ProgressSummary', innerError)
//       }

//       setAllRows(rows)
//       setFilteredRows(rows)
//       showMessage({
//         type: MESSAGE_TYPE.SUCCESS,
//         text: 'Mostrando Ztudiantes'
//       })
//     } catch (error) {
//       await LogErrors('GetFilteredStudents - Progress', error)
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: 'Ha habido un error al intentar obtener el listado de Ztudiantes.'
//       })
//       setAllRows([])
//       setFilteredRows([])
//     } finally {
//       setIsWorking(false)
//       setIsLoading(false)
//     }
//   }

  
//   return (
//     isLoading ? (
//       <Loading />
//     ) : (
//       <InstructorBase urlToGo={'/dashboard'} idHelp={HELP_TYPE.INSTRUCTOR.PROGRESS} page={WEBPAGES.INSTRUCTOR.PROGRESS}>
//         <section className='container Instructor__Reports'>
//           <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>
//             Progreso de mis Ztudiantes
//           </p>

//           {/* Filtros encadenados */}
//           <div className='Instructor__Progress__Filters'>
//             {/* CURSO */}
//             {isCoursesListLoading ? (
//               <select className='Instructor__Progress__Filters__List' disabled={true}>
//                 <option>Cargando lista...</option>
//               </select>
//             ) : (
//               <select
//                 className='Instructor__Progress__Filters__List'
//                 value={selectedCourseId}
//                 onChange={(e) => handleSelectCourse(parseInt(e.target.value, 10))}
//                 disabled={isWorking || !courses}
//               >
//                 <option value={0}>Selecciona un curso</option>
//                 {(courses ?? []).map(c => (
//                   <option key={c.id} value={c.id}>{c.name}{c.level !== '' && ` nivel ${c.level}`}</option>
//                 ))}
//               </select>
//             )}

//             {/* GRUPO */}
//             {isGroupsListLoading ? (
//               <select className='Instructor__Progress__Filters__List' disabled={true}>
//                 <option>Cargando lista...</option>
//               </select>
//             ) : (
//               <select
//                 className='Instructor__Progress__Filters__List'
//                 value={selectedGroupId}
//                 onChange={(e) => handleSelectGroup(parseInt(e.target.value, 10))}
//                 disabled={isWorking || !selectedCourseId || !groups}
//               >
//                 <option value={0}>Selecciona un grupo</option>
//                 {(groups ?? []).map(g => (
//                   <option key={g.idGroup} value={g.idGroup}>{g.groupName}</option>
//                 ))}
//               </select>
//             )}

//             {/* APLICAR FILTRO */}
//             <button
//               type='button'
//               className='Btn__Ztudia--PrimaryOutline Instructor__Progress__Filters__Button'
//               onClick={GetFilteredStudents}
//               disabled={
//                 isWorking ||
//                 !selectedCourseId ||
//                 selectedCourseId === 0 ||
//                 !selectedGroupId ||
//                 selectedGroupId === 0
//               }
//             >
//               <i className='bi bi-funnel'></i> Aplicar filtro
//             </button>
//           </div>

//           {/* Buscador */}
//           <Search
//             onChange={setSearchText}
//             extraClassName={'Instructor__Reports__Ztudents__Search mt-5 mw-100'}
//           />

//           {/* Botón Exportar (tabla completa) */}
//           <div className='Instructor__SpaceUp Instructor__Progress__ExportTable'>
//             <button
//               type='button'
//               className='Btn__Ztudia--PrimaryOutline Instructor__Progress__ExportTable__Button'
//               onClick={ExportGroupTableToExcel}
//               disabled={isWorking || !filteredRows || filteredRows.length === 0}
//             >
//               <i className='bi bi-file-earmark-excel'></i> Exportar
//             </button>
//           </div>

//           {/* Tabla (sin avance visible todavía) */}
//           <section className='Tables__Container Instructor__Progress--V2__ContainerTable Tables--Shadow Tables--Background'>
//             <table className='Tables__Table Instructor__Progress--V2__ContainerTable__Table'>
//               <thead>
//                 <tr className='Tables__Table__Row--Header'>
//                   <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>Nombre</td>
//                   <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>Usuario</td>
//                   <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>Avance</td>
//                   <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn'></td>
//                   <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn'></td>
//                 </tr>
//               </thead>
//               <tbody>
//                 {filteredRows?.length ? (
//                   filteredRows.map((r, idx) => (
//                     <tr key={r.idZtudiante ?? idx} className='Tables__Table__Row'>
//                       <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>
//                         {`${r.name ?? ''} ${r.lastName ?? ''}`.trim()}
//                       </td>
//                       <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>
//                         {r.user}
//                       </td>
//                       <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__TextColumn'>
//                         {r.generalProgress ?? ''}
//                       </td>
//                       <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn'>
//                         <div className='Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn__Buttons'>
//                           <button type='button' className='Btn__Ztudia--PrimaryOutline Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn__Buttons__Button' onClick={() => GoToNextView(r)}>Ver más</button>
//                         </div>
//                       </td>
//                       <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn'>
//                         <div className='Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn__Buttons'>
//                           <button type='button' className='Btn__Ztudia--PrimaryOutline Instructor__Progress--V2__ContainerTable__Table__ActionButtonsColumn__Buttons__Button' onClick={() => ExportIndividualToExcel(r)} disabled={isWorking}><i className='bi bi-file-earmark-excel'></i> Exportar</button>
//                         </div>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr className='Tables__Table__Row'>
//                     <td className='Tables__Table__Row__Cell' colSpan={5}>
//                       {isWorking ? 'Cargando...' : 'Sin resultados por mostrar.'}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </section>

//           {/* Total de registros mostrados en la tabla */}
//           <p className='Instructor__Subtitle Instructor__Subtitle--Center Instructor__SpaceUp'>
//             Total: {filteredRows?.length ?? 0}
//           </p>

//           {errorMessage && errorMessage !== '' && (
//             <p className='Instructor__Message--Error'>{errorMessage}</p>
//           )}
//         </section>
//       </InstructorBase>
//     )
//   )
// }

// export { Progress };